/**
 * Hashtag tokenization and matching.
 *
 * A hashtag is a whole token: `#Fit` and `#Fitness` are different tags, and
 * `#fit` is the same tag as `#Fit`. Matching therefore compares normalized
 * whole tokens — never a substring test — while the label keeps the casing the
 * author typed.
 */

/**
 * A tag token: letters, digits and underscore, in any script. `#` may only
 * start a token (not mid-word, so `a#b` is not a tag) and the token must not
 * run straight into more word characters.
 */
const HASHTAG_PATTERN = /(^|[^\p{L}\p{N}_#])#([\p{L}\p{N}_]+)/gu;

const HTML_TAG_PATTERN = /<[^>]*>/g;

/** Comparison key for a tag, with or without a leading `#`. */
export const normalizeHashtag = (value: string): string =>
  value
    .replace(/^#+/, '')
    .trim()
    .toLowerCase();

/** Display form, exactly one leading `#`, author casing preserved. */
export const formatHashtag = (value: string): string => {
  const label = value.replace(/^#+/, '').trim();
  return label ? `#${label}` : '';
};

/**
 * Every tag token in a body. HTML bodies are stripped first so attribute
 * values and markup never contribute tokens.
 */
export function extractHashtags(text: string | null | undefined): string[] {
  if (!text) return [];
  const plain = text.includes('<') ? text.replace(HTML_TAG_PATTERN, ' ') : text;
  const tags: string[] = [];
  for (const match of plain.matchAll(HASHTAG_PATTERN)) {
    tags.push(match[2]);
  }
  return tags;
}

/** Tags of a post: the API list plus anything written in the body. */
export function collectPostHashtags(post: {
  hashtags?: string[] | null;
  text?: string | null;
  content?: string | null;
}): string[] {
  const fromApi = (post.hashtags ?? [])
    .flatMap(tag => String(tag).split(/[,\s]+/))
    .map(tag => tag.replace(/^#+/, '').trim())
    .filter(Boolean);
  return [...fromApi, ...extractHashtags(post.text ?? post.content ?? '')];
}

/**
 * Whole-token, case-insensitive match. Used to keep a hashtag-filtered feed
 * honest when the API returns loosely matched posts.
 */
export function postMatchesHashtag(
  post: { hashtags?: string[] | null; text?: string | null; content?: string | null },
  hashtag: string,
): boolean {
  return postMatchesAnyHashtag(post, parseHashtagFilter(hashtag));
}

/**
 * Tags of an applied filter. The Filter Posts sheet can apply several at once
 * as one comma-separated search string.
 */
export function parseHashtagFilter(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(',').map(normalizeHashtag).filter(Boolean);
}

/** True when the post carries any of the filtered tags, as a whole token. */
export function postMatchesAnyHashtag(
  post: { hashtags?: string[] | null; text?: string | null; content?: string | null },
  hashtags: readonly string[],
): boolean {
  if (!hashtags.length) return true;
  const postTags = new Set(collectPostHashtags(post).map(normalizeHashtag));
  return hashtags.some(tag => postTags.has(normalizeHashtag(tag)));
}
